import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Avatar,
    Box,
    Chip,
    CircularProgress,
    Grid,
    InputAdornment,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
    useTheme,
    Switch,
    Tooltip,
} from "@mui/material";

import { alpha } from "@mui/material/styles";

import SearchIcon from "@mui/icons-material/Search";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import PixOutlinedIcon from "@mui/icons-material/Pix";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";

import { tokens } from "../../theme";

import {
    fetchManagerPersonalizedFinancialMetrics,
    fetchManagerPersonalizedOrderSummary,
    updateManagerPersonalizedOrderPaymentChecked,
} from "../../service/personalizedManager.service";

const STATUS_LABELS = {
    PENDING: "Pendente",
    GENERATED: "Gerado",
    PAID: "Pago",
    EXPIRED: "Expirado",
    CANCELED: "Cancelado",
    ERROR: "Erro",
};

const PAYMENT_LABELS = {
    PIX: "Pix",
    PAYMENT_LINK: "Link",
};

const VIEW_FILTERS = {
    RECEIVED: "RECEIVED",
    WITH_TRANSACTION: "WITH_TRANSACTION",
    WITHOUT_TRANSACTION: "WITHOUT_TRANSACTION",
    ALL: "ALL",
};

const moneyBRL = (value) =>
    Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
    });

const normalize = (value) =>
    String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();

const parseDateValue = (value) => {
    if (!value) {
        return 0;
    }

    const timestamp = new Date(value).getTime();

    return Number.isNaN(timestamp)
        ? 0
        : timestamp;
};

const formatDateTimeBR = (value) => {
    if (!value) {
        return "-";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return "-";
    }

    return new Intl.DateTimeFormat("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    }).format(date);
};

const getSellerName = (row) =>
    row?.seller_name ||
    row?.seller?.name ||
    row?.seller?.nome ||
    row?.reseller_name ||
    row?.revendedora_name ||
    row?.seller_display_name ||
    "-";

const getPaymentDate = (row) =>
    row?.last_transaction_paid_at ||
    row?.last_transaction_created_at ||
    row?.created_at ||
    null;

const getPaymentSortDate = (row) => {
    const paidAt = parseDateValue(
        row?.last_transaction_paid_at
    );

    if (paidAt) {
        return paidAt;
    }

    const transactionCreatedAt = parseDateValue(
        row?.last_transaction_created_at
    );

    if (transactionCreatedAt) {
        return transactionCreatedAt;
    }

    return parseDateValue(row?.created_at);
};

const hasTransaction = (row) =>
    Number(row?.transactions_count || 0) > 0;

const hasReceivedPayment = (row) =>
    Number(row?.total_paid || 0) > 0 ||
    Number(row?.paid_count || 0) > 0 ||
    row?.last_transaction_status === "PAID";

const getStatusPresentation = (
    status,
    colors,
    theme
) => {
    switch (status) {
        case "PAID":
            return {
                label: "Pago",
                icon: (
                    <CheckCircleOutlineOutlinedIcon
                        sx={{ fontSize: 16 }}
                    />
                ),
                background:
                    theme.palette.mode === "dark"
                        ? "linear-gradient(135deg, rgba(34,197,94,0.22) 0%, rgba(16,185,129,0.12) 100%)"
                        : "linear-gradient(135deg, rgba(34,197,94,0.15) 0%, rgba(16,185,129,0.08) 100%)",
                color: colors.greenAccent[400],
                border: alpha(
                    colors.greenAccent[400],
                    0.3
                ),
            };

        case "PENDING":
        case "GENERATED":
            return {
                label:
                    STATUS_LABELS[status] ||
                    status,
                icon: (
                    <HourglassBottomOutlinedIcon
                        sx={{ fontSize: 16 }}
                    />
                ),
                background:
                    "linear-gradient(135deg, rgba(245,158,11,0.16) 0%, rgba(217,119,6,0.08) 100%)",
                color: "#F59E0B",
                border: "rgba(245,158,11,0.28)",
            };

        case "EXPIRED":
        case "ERROR":
            return {
                label:
                    STATUS_LABELS[status] ||
                    status,
                icon: (
                    <ErrorOutlineOutlinedIcon
                        sx={{ fontSize: 16 }}
                    />
                ),
                background:
                    "linear-gradient(135deg, rgba(239,68,68,0.16) 0%, rgba(220,38,38,0.08) 100%)",
                color: colors.redAccent[400],
                border: alpha(
                    colors.redAccent[400],
                    0.28
                ),
            };

        case "CANCELED":
            return {
                label: "Cancelado",
                icon: (
                    <CancelOutlinedIcon
                        sx={{ fontSize: 16 }}
                    />
                ),
                background:
                    "linear-gradient(135deg, rgba(244,63,94,0.16) 0%, rgba(190,24,93,0.08) 100%)",
                color: "#FB7185",
                border: "rgba(251,113,133,0.28)",
            };

        default:
            return {
                label: "Sem cobrança",
                icon: (
                    <AutorenewOutlinedIcon
                        sx={{ fontSize: 16 }}
                    />
                ),
                background:
                    "linear-gradient(135deg, rgba(148,163,184,0.14) 0%, rgba(100,116,139,0.08) 100%)",
                color: colors.grey[400],
                border: alpha(
                    colors.grey[300],
                    0.18
                ),
            };
    }
};

const getPaymentMethodPresentation = (
    method,
    colors
) => {
    switch (method) {
        case "PIX":
            return {
                label: "Pix",
                icon: (
                    <PixOutlinedIcon
                        sx={{ fontSize: 15 }}
                    />
                ),
                color: colors.greenAccent[400],
                background: alpha(
                    colors.greenAccent[400],
                    0.12
                ),
                border: alpha(
                    colors.greenAccent[400],
                    0.22
                ),
            };

        case "PAYMENT_LINK":
            return {
                label: "Link",
                icon: (
                    <LinkOutlinedIcon
                        sx={{ fontSize: 15 }}
                    />
                ),
                color:
                    colors.blueAccent?.[400] ||
                    "#60A5FA",
                background:
                    "rgba(59,130,246,0.12)",
                border:
                    "rgba(59,130,246,0.22)",
            };

        default:
            return {
                label: "-",
                icon: null,
                color: colors.grey[400],
                background: alpha(
                    colors.grey[300],
                    0.08
                ),
                border: alpha(
                    colors.grey[300],
                    0.14
                ),
            };
    }
};

function MetricCard({
    title,
    value,
    subtitle,
    icon,
    accent,
    colors,
}) {
    return (
        <Paper
            elevation={0}
            sx={{
                height: "100%",
                p: 2,
                borderRadius: "18px",
                position: "relative",
                overflow: "hidden",
                border: `1px solid ${alpha(
                    accent,
                    0.22
                )}`,
                background: `linear-gradient(
                    135deg,
                    ${alpha(accent, 0.2)} 0%,
                    ${alpha(accent, 0.08)} 48%,
                    ${colors.primary[400]} 100%
                )`,
                boxShadow: `0 10px 24px ${alpha(
                    accent,
                    0.1
                )}`,
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    width: 90,
                    height: 90,
                    borderRadius: "50%",
                    right: -25,
                    top: -25,
                    backgroundColor: alpha(
                        "#fff",
                        0.05
                    ),
                }}
            />

            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={2}
                position="relative"
            >
                <Box minWidth={0}>
                    <Typography
                        sx={{
                            color: alpha(
                                "#fff",
                                0.7
                            ),
                            fontWeight: 900,
                            fontSize: "0.72rem",
                            letterSpacing: 0.5,
                            textTransform:
                                "uppercase",
                        }}
                    >
                        {title}
                    </Typography>

                    <Typography
                        variant="h4"
                        sx={{
                            color: "#fff",
                            fontWeight: 900,
                            mt: 0.7,
                            lineHeight: 1.15,
                        }}
                    >
                        {value}
                    </Typography>

                    <Typography
                        variant="caption"
                        sx={{
                            display: "block",
                            color: alpha(
                                "#fff",
                                0.65
                            ),
                            mt: 0.8,
                        }}
                    >
                        {subtitle}
                    </Typography>
                </Box>

                <Avatar
                    sx={{
                        width: 46,
                        height: 46,
                        bgcolor: alpha(
                            "#fff",
                            0.1
                        ),
                        color: "#fff",
                    }}
                >
                    {icon}
                </Avatar>
            </Stack>
        </Paper>
    );
}

export default function AdminPersonalizedDashboard() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [loading, setLoading] =
        useState(true);

    const [baseMetrics, setBaseMetrics] =
        useState(null);

    const [allRows, setAllRows] =
        useState([]);

    const [search, setSearch] =
        useState("");

    const [viewFilter, setViewFilter] =
        useState(VIEW_FILTERS.RECEIVED);

    const [statusFilter, setStatusFilter] =
        useState("");

    const [paymentFilter, setPaymentFilter] =
        useState("");

    const [page, setPage] =
        useState(0);

    const [rowsPerPage, setRowsPerPage] =
        useState(20);

    const [checkingOrderIds, setCheckingOrderIds] =
        useState(new Set());

    const [checkedFilter, setCheckedFilter] =
        useState("");

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);

                const [
                    metricsResp,
                    summaryResp,
                ] = await Promise.all([
                    fetchManagerPersonalizedFinancialMetrics(),
                    fetchManagerPersonalizedOrderSummary(),
                ]);

                setBaseMetrics(
                    metricsResp || null
                );

                setAllRows(
                    Array.isArray(summaryResp)
                        ? summaryResp
                        : []
                );
            } catch (error) {
                console.error(
                    "Erro ao carregar controle financeiro:",
                    error?.response?.data ||
                    error
                );
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, []);

    const filteredRows = useMemo(() => {
        const term = normalize(search);

        return allRows
            .filter((row) => {
                const sellerName =
                    getSellerName(row);

                const matchesChecked =
                    checkedFilter === ""
                        ? true
                        : checkedFilter === "checked"
                            ? Boolean(
                                row.is_payment_checked
                            )
                            : !Boolean(
                                row.is_payment_checked
                            );

                const matchesSearch =
                    !term ||
                    [
                        row.order_code,
                        row.customer_name,
                        row.customer_phone,
                        row.last_transaction_status,
                        row.last_transaction_status_label,
                        row.last_payment_method,
                        row.last_payment_method_label,
                        sellerName,
                    ]
                        .filter(Boolean)
                        .some((field) =>
                            normalize(
                                field
                            ).includes(term)
                        );

                const matchesView =
                    viewFilter ===
                        VIEW_FILTERS.ALL
                        ? true
                        : viewFilter ===
                            VIEW_FILTERS.RECEIVED
                            ? hasReceivedPayment(row)
                            : viewFilter ===
                                VIEW_FILTERS.WITH_TRANSACTION
                                ? hasTransaction(row)
                                : viewFilter ===
                                    VIEW_FILTERS.WITHOUT_TRANSACTION
                                    ? !hasTransaction(row)
                                    : true;

                const matchesStatus =
                    !statusFilter ||
                    row.last_transaction_status ===
                    statusFilter;

                const matchesPayment =
                    !paymentFilter ||
                    row.last_payment_method ===
                    paymentFilter;

                return (
                    matchesSearch &&
                    matchesView &&
                    matchesStatus &&
                    matchesPayment &&
                    matchesChecked
                );
            })
            .sort((a, b) => {
                const aHasTransaction =
                    hasTransaction(a);

                const bHasTransaction =
                    hasTransaction(b);

                if (
                    aHasTransaction !==
                    bHasTransaction
                ) {
                    return aHasTransaction
                        ? -1
                        : 1;
                }

                const aPaid =
                    hasReceivedPayment(a);

                const bPaid =
                    hasReceivedPayment(b);

                if (aPaid !== bPaid) {
                    return aPaid ? -1 : 1;
                }

                return (
                    getPaymentSortDate(b) -
                    getPaymentSortDate(a)
                );
            });
    }, [
        allRows,
        search,
        viewFilter,
        statusFilter,
        paymentFilter,
        checkedFilter
    ]);

    useEffect(() => {
        setPage(0);
    }, [
        search,
        viewFilter,
        statusFilter,
        paymentFilter,
        checkedFilter,
    ]);

    const paginatedRows = useMemo(() => {
        const start =
            page * rowsPerPage;

        return filteredRows.slice(
            start,
            start + rowsPerPage
        );
    }, [
        filteredRows,
        page,
        rowsPerPage,
    ]);

    const localMetrics = useMemo(() => {
        const ordersCount =
            filteredRows.length;

        const ordersTotal =
            filteredRows.reduce(
                (sum, row) =>
                    sum +
                    Number(
                        row.order_total || 0
                    ),
                0
            );

        const chargedTotal =
            filteredRows.reduce(
                (sum, row) =>
                    sum +
                    Number(
                        row.total_charged || 0
                    ),
                0
            );

        const receivedTotal =
            filteredRows.reduce(
                (sum, row) =>
                    sum +
                    Number(
                        row.total_paid || 0
                    ),
                0
            );

        const transactionCount =
            filteredRows.reduce(
                (sum, row) =>
                    sum +
                    Number(
                        row.transactions_count ||
                        0
                    ),
                0
            );

        const receivedOrders =
            filteredRows.filter(
                hasReceivedPayment
            ).length;

        return {
            ordersCount,
            ordersTotal,
            chargedTotal,
            receivedTotal,
            transactionCount,
            receivedOrders,
        };
    }, [filteredRows]);

    const handleTogglePaymentChecked = async (
        orderId,
        nextChecked
    ) => {
        const previousRow = allRows.find(
            (row) => row.order_id === orderId
        );

        if (!previousRow) {
            return;
        }

        setCheckingOrderIds((current) => {
            const next = new Set(current);
            next.add(orderId);
            return next;
        });

        setAllRows((currentRows) =>
            currentRows.map((row) =>
                row.order_id === orderId
                    ? {
                        ...row,
                        is_payment_checked:
                            nextChecked,
                    }
                    : row
            )
        );

        try {
            const updated =
                await updateManagerPersonalizedOrderPaymentChecked(
                    orderId,
                    nextChecked
                );

            setAllRows((currentRows) =>
                currentRows.map((row) =>
                    row.order_id === orderId
                        ? {
                            ...row,
                            is_payment_checked:
                                updated.is_payment_checked,
                            payment_checked_at:
                                updated.payment_checked_at,
                            payment_checked_by:
                                updated.payment_checked_by,
                        }
                        : row
                )
            );
        } catch (error) {
            console.error(
                "Erro ao atualizar conferência:",
                error?.response?.data ||
                error
            );

            setAllRows((currentRows) =>
                currentRows.map((row) =>
                    row.order_id === orderId
                        ? previousRow
                        : row
                )
            );
        } finally {
            setCheckingOrderIds(
                (current) => {
                    const next = new Set(
                        current
                    );

                    next.delete(orderId);

                    return next;
                }
            );
        }
    };

    const headerBackground =
        theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #14070C 0%, #2A0D17 48%, #6E1B38 100%)"
            : "linear-gradient(135deg, #fff4f8 0%, #ffd7e6 52%, #ffc7dc 100%)";

    const tableHeadBackground =
        theme.palette.mode === "dark"
            ? "#0F172A"
            : "#FFF7FA";

    if (loading) {
        return (
            <Box
                width="100%"
                minHeight="55vh"
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                <Stack
                    alignItems="center"
                    spacing={2}
                >
                    <CircularProgress
                        color="success"
                    />

                    <Typography
                        color={
                            colors.grey[400]
                        }
                        fontWeight={700}
                    >
                        Carregando pagamentos...
                    </Typography>
                </Stack>
            </Box>
        );
    }

    return (
        <Box
            width="100%"
            p={{ xs: 1, md: 2 }}
            pt="1px"
        >
            <Paper
                elevation={0}
                sx={{
                    mb: 2,
                    p: {
                        xs: 2,
                        md: 2.5,
                    },
                    borderRadius: "22px",
                    background:
                        headerBackground,
                    border: `1px solid ${alpha(
                        "#fff",
                        theme.palette.mode ===
                            "dark"
                            ? 0.08
                            : 0.5
                    )}`,
                    boxShadow:
                        theme.palette.mode ===
                            "dark"
                            ? "0 18px 50px rgba(0,0,0,0.26)"
                            : "0 16px 40px rgba(148,24,77,0.10)",
                }}
            >
                <Stack
                    direction={{
                        xs: "column",
                        md: "row",
                    }}
                    justifyContent="space-between"
                    alignItems={{
                        xs: "flex-start",
                        md: "center",
                    }}
                    spacing={2}
                >
                    <Stack
                        direction="row"
                        spacing={1.4}
                        alignItems="center"
                    >
                        <Avatar
                            sx={{
                                width: 48,
                                height: 48,
                                bgcolor: alpha(
                                    "#fff",
                                    0.14
                                ),
                                color: "#fff",
                            }}
                        >
                            <TrendingUpOutlinedIcon />
                        </Avatar>

                        <Box>
                            <Typography
                                variant="h4"
                                fontWeight={900}
                                sx={{
                                    color:
                                        theme.palette
                                            .mode ===
                                            "dark"
                                            ? "#fff"
                                            : "#6E1B38",
                                    lineHeight: 1.15,
                                }}
                            >
                                Controle de pagamentos
                            </Typography>

                        </Box>
                    </Stack>

                    <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        flexWrap="wrap"
                    >
                        <Chip
                            label={`${baseMetrics?.orders_count || 0} pedidos`}
                            sx={{
                                fontWeight: 900,
                                bgcolor: alpha(
                                    "#fff",
                                    0.12
                                ),
                                color:
                                    theme.palette
                                        .mode ===
                                        "dark"
                                        ? "#fff"
                                        : "#6E1B38",
                            }}
                        />

                        <Chip
                            label={`${baseMetrics?.transactions_count || 0} cobranças`}
                            sx={{
                                fontWeight: 900,
                                bgcolor: alpha(
                                    "#fff",
                                    0.12
                                ),
                                color:
                                    theme.palette
                                        .mode ===
                                        "dark"
                                        ? "#fff"
                                        : "#6E1B38",
                            }}
                        />

                        <Chip
                            label={`Exibindo ${filteredRows.length}`}
                            sx={{
                                fontWeight: 900,
                                bgcolor: alpha(
                                    "#fff",
                                    0.12
                                ),
                                color:
                                    theme.palette
                                        .mode ===
                                        "dark"
                                        ? "#fff"
                                        : "#6E1B38",
                            }}
                        />
                    </Stack>
                </Stack>
            </Paper>

            <Grid
                container
                spacing={1.6}
                mb={2}
            >
                <Grid
                    item
                    xs={12}
                    sm={6}
                    xl={3}
                >
                    <MetricCard
                        title="Total dos pedidos"
                        value={moneyBRL(
                            baseMetrics?.orders_total
                        )}
                        subtitle={`${baseMetrics?.orders_count || 0} pedido(s) na base`}
                        icon={
                            <ReceiptLongOutlinedIcon />
                        }
                        accent="#A855F7"
                        colors={colors}
                    />
                </Grid>

                <Grid
                    item
                    xs={12}
                    sm={6}
                    xl={3}
                >
                    <MetricCard
                        title="Total cobrado"
                        value={moneyBRL(
                            baseMetrics?.transactions_total
                        )}
                        subtitle={`${baseMetrics?.transactions_count || 0} cobrança(s) gerada(s)`}
                        icon={
                            <PointOfSaleOutlinedIcon />
                        }
                        accent="#06B6D4"
                        colors={colors}
                    />
                </Grid>

                <Grid
                    item
                    xs={12}
                    sm={6}
                    xl={3}
                >
                    <MetricCard
                        title="Total recebido"
                        value={moneyBRL(
                            baseMetrics?.received_total
                        )}
                        subtitle="Pagamentos efetivamente confirmados"
                        icon={
                            <PaidOutlinedIcon />
                        }
                        accent="#22C55E"
                        colors={colors}
                    />
                </Grid>

                <Grid
                    item
                    xs={12}
                    sm={6}
                    xl={3}
                >
                    <MetricCard
                        title="Saldo a receber"
                        value={moneyBRL(
                            Number(
                                baseMetrics?.transactions_total ||
                                0
                            ) -
                            Number(
                                baseMetrics?.received_total ||
                                0
                            )
                        )}
                        subtitle="Diferença entre o cobrado e o recebido"
                        icon={
                            <AccountBalanceWalletOutlinedIcon />
                        }
                        accent="#F59E0B"
                        colors={colors}
                    />
                </Grid>
            </Grid>

            <Paper
                elevation={0}
                sx={{
                    p: 1.8,
                    mb: 2,
                    borderRadius: "18px",
                    border: `1px solid ${alpha(
                        colors.grey[100],
                        0.08
                    )}`,
                    background:
                        theme.palette.mode ===
                            "dark"
                            ? `linear-gradient(180deg, ${alpha(
                                "#111827",
                                0.94
                            )} 0%, ${alpha(
                                "#0F172A",
                                0.98
                            )} 100%)`
                            : "#fff",
                }}
            >
                <Box
                    display="grid"
                    gridTemplateColumns={{
                        xs: "1fr",
                        md: "1.7fr 1fr 1fr 1fr 1fr",
                    }}
                    gap={1.5}
                >
                    <TextField
                        fullWidth
                        size="small"
                        label="Buscar pedido, cliente, telefone ou vendedora"
                        value={search}
                        onChange={(event) =>
                            setSearch(
                                event.target.value
                            )
                        }
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Visualização"
                        value={viewFilter}
                        onChange={(event) =>
                            setViewFilter(
                                event.target.value
                            )
                        }
                    >
                        <MenuItem
                            value={
                                VIEW_FILTERS.RECEIVED
                            }
                        >
                            Pagamentos recebidos
                        </MenuItem>

                        <MenuItem
                            value={
                                VIEW_FILTERS.WITH_TRANSACTION
                            }
                        >
                            Com cobrança
                        </MenuItem>

                        <MenuItem
                            value={
                                VIEW_FILTERS.WITHOUT_TRANSACTION
                            }
                        >
                            Sem cobrança
                        </MenuItem>

                        <MenuItem
                            value={
                                VIEW_FILTERS.ALL
                            }
                        >
                            Todos os pedidos
                        </MenuItem>
                    </TextField>

                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Status"
                        value={statusFilter}
                        onChange={(event) =>
                            setStatusFilter(
                                event.target.value
                            )
                        }
                    >
                        <MenuItem value="">
                            Todos
                        </MenuItem>

                        {Object.entries(
                            STATUS_LABELS
                        ).map(
                            ([key, label]) => (
                                <MenuItem
                                    key={key}
                                    value={key}
                                >
                                    {label}
                                </MenuItem>
                            )
                        )}
                    </TextField>

                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Método"
                        value={paymentFilter}
                        onChange={(event) =>
                            setPaymentFilter(
                                event.target.value
                            )
                        }
                    >
                        <MenuItem value="">
                            Todos
                        </MenuItem>

                        {Object.entries(
                            PAYMENT_LABELS
                        ).map(
                            ([key, label]) => (
                                <MenuItem
                                    key={key}
                                    value={key}
                                >
                                    {label}
                                </MenuItem>
                            )
                        )}
                    </TextField>
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Conferência"
                        value={checkedFilter}
                        onChange={(event) =>
                            setCheckedFilter(
                                event.target.value
                            )
                        }
                    >
                        <MenuItem value="">
                            Todos
                        </MenuItem>

                        <MenuItem value="unchecked">
                            Pendentes
                        </MenuItem>

                        <MenuItem value="checked">
                            Conferidos
                        </MenuItem>
                    </TextField>
                </Box>
            </Paper>

            <Paper
                elevation={0}
                sx={{
                    borderRadius: "20px",
                    overflow: "hidden",
                    border: `1px solid ${alpha(
                        colors.grey[100],
                        0.08
                    )}`,
                    background:
                        theme.palette.mode ===
                            "dark"
                            ? "#0B1220"
                            : "#fff",
                    boxShadow:
                        theme.palette.mode ===
                            "dark"
                            ? "0 12px 30px rgba(0,0,0,0.22)"
                            : "0 10px 24px rgba(15,23,42,0.06)",
                }}
            >
                <Box
                    sx={{
                        px: 2,
                        py: 1.4,
                        borderBottom: `1px solid ${alpha(
                            colors.grey[100],
                            0.08
                        )}`,
                    }}
                >
                    <Stack
                        direction={{
                            xs: "column",
                            sm: "row",
                        }}
                        justifyContent="space-between"
                        alignItems={{
                            xs: "flex-start",
                            sm: "center",
                        }}
                        spacing={1}
                    >
                        <Box>
                            <Typography
                                fontWeight={900}
                                color={
                                    colors.grey[100]
                                }
                            >
                                Movimentações financeiras
                            </Typography>

                            <Typography
                                variant="caption"
                                color={
                                    colors.grey[400]
                                }
                            >
                                Ordenação: pagamento confirmado mais recente primeiro.
                            </Typography>
                        </Box>

                        <Stack
                            direction="row"
                            spacing={1}
                            useFlexGap
                            flexWrap="wrap"
                        >
                            <Chip
                                size="small"
                                label={`${localMetrics.receivedOrders} pedido(s) recebido(s)`}
                                sx={{
                                    fontWeight: 900,
                                    bgcolor: alpha(
                                        colors.greenAccent[400],
                                        0.12
                                    ),
                                    color:
                                        colors.greenAccent[400],
                                }}
                            />

                            <Chip
                                size="small"
                                label={moneyBRL(
                                    localMetrics.receivedTotal
                                )}
                                sx={{
                                    fontWeight: 900,
                                    bgcolor: alpha(
                                        colors.greenAccent[400],
                                        0.12
                                    ),
                                    color:
                                        colors.greenAccent[400],
                                }}
                            />
                        </Stack>
                    </Stack>
                </Box>

                <TableContainer
                    sx={{
                        maxHeight: "65vh",
                        overflowY: "auto",
                        overflowX: "auto",
                    }}
                >
                    <Table
                        stickyHeader
                        size="small"
                        sx={{
                            minWidth: 1280,
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    align="center"
                                    sx={{
                                        minWidth: 130,
                                        fontWeight: 900,
                                        backgroundColor:
                                            tableHeadBackground,
                                    }}
                                >
                                    Conferência
                                </TableCell>
                                <TableCell
                                    sx={{
                                        minWidth: 155,
                                        fontWeight: 900,
                                        backgroundColor:
                                            tableHeadBackground,
                                    }}
                                >
                                    Entrada
                                </TableCell>

                                <TableCell
                                    align="right"
                                    sx={{
                                        minWidth: 130,
                                        fontWeight: 900,
                                        backgroundColor:
                                            tableHeadBackground,
                                    }}
                                >
                                    Recebido
                                </TableCell>

                                <TableCell
                                    align="center"
                                    sx={{
                                        minWidth: 125,
                                        fontWeight: 900,
                                        backgroundColor:
                                            tableHeadBackground,
                                    }}
                                >
                                    Status
                                </TableCell>

                                <TableCell
                                    align="center"
                                    sx={{
                                        minWidth: 100,
                                        fontWeight: 900,
                                        backgroundColor:
                                            tableHeadBackground,
                                    }}
                                >
                                    Método
                                </TableCell>

                                <TableCell
                                    sx={{
                                        minWidth: 180,
                                        fontWeight: 900,
                                        backgroundColor:
                                            tableHeadBackground,
                                    }}
                                >
                                    Pedido
                                </TableCell>

                                <TableCell
                                    sx={{
                                        minWidth: 240,
                                        fontWeight: 900,
                                        backgroundColor:
                                            tableHeadBackground,
                                    }}
                                >
                                    Cliente
                                </TableCell>

                                <TableCell
                                    sx={{
                                        minWidth: 220,
                                        fontWeight: 900,
                                        backgroundColor:
                                            tableHeadBackground,
                                    }}
                                >
                                    Vendedora
                                </TableCell>

                                <TableCell
                                    align="right"
                                    sx={{
                                        minWidth: 125,
                                        fontWeight: 900,
                                        backgroundColor:
                                            tableHeadBackground,
                                    }}
                                >
                                    Pedido
                                </TableCell>

                                <TableCell
                                    align="right"
                                    sx={{
                                        minWidth: 125,
                                        fontWeight: 900,
                                        backgroundColor:
                                            tableHeadBackground,
                                    }}
                                >
                                    Cobrado
                                </TableCell>

                                <TableCell
                                    align="right"
                                    sx={{
                                        minWidth: 125,
                                        fontWeight: 900,
                                        backgroundColor:
                                            tableHeadBackground,
                                    }}
                                >
                                    Saldo
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {paginatedRows.map(
                                (row) => {
                                    const statusUI =
                                        getStatusPresentation(
                                            row.last_transaction_status,
                                            colors,
                                            theme
                                        );

                                    const paymentUI =
                                        getPaymentMethodPresentation(
                                            row.last_payment_method,
                                            colors
                                        );

                                    const sellerName =
                                        getSellerName(
                                            row
                                        );

                                    const paymentDate =
                                        getPaymentDate(
                                            row
                                        );

                                    const received =
                                        Number(
                                            row.total_paid ||
                                            0
                                        );

                                    const balance =
                                        Number(
                                            row.payment_balance ??
                                            Number(
                                                row.order_total ||
                                                0
                                            ) -
                                            received
                                        );

                                    const rowIsPaid =
                                        hasReceivedPayment(
                                            row
                                        );

                                    return (
                                        <TableRow
                                            key={
                                                row.order_id
                                            }
                                            hover
                                            sx={{
                                                backgroundColor:
                                                    rowIsPaid
                                                        ? alpha(
                                                            colors.greenAccent[500],
                                                            theme
                                                                .palette
                                                                .mode ===
                                                                "dark"
                                                                ? 0.035
                                                                : 0.025
                                                        )
                                                        : "transparent",
                                                "&:hover":
                                                {
                                                    backgroundColor:
                                                        theme
                                                            .palette
                                                            .mode ===
                                                            "dark"
                                                            ? alpha(
                                                                "#fff",
                                                                0.035
                                                            )
                                                            : alpha(
                                                                "#6E1B38",
                                                                0.035
                                                            ),
                                                },
                                            }}
                                        >
                                            <TableCell align="center">
                                                {checkingOrderIds.has(
                                                    row.order_id
                                                ) ? (
                                                    <CircularProgress
                                                        size={20}
                                                        color="success"
                                                    />
                                                ) : (
                                                    <Stack
                                                        alignItems="center"
                                                        spacing={0.15}
                                                    >
                                                        <Tooltip
                                                            title={
                                                                row.is_payment_checked
                                                                    ? "Desmarcar pedido"
                                                                    : "Marcar pedido como conferido"
                                                            }
                                                        >
                                                            <Switch
                                                                checked={Boolean(
                                                                    row.is_payment_checked
                                                                )}
                                                                onChange={(event) =>
                                                                    handleTogglePaymentChecked(
                                                                        row.order_id,
                                                                        event.target.checked
                                                                    )
                                                                }
                                                                color="success"
                                                                size="small"
                                                            />
                                                        </Tooltip>

                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontWeight: 800,
                                                                color:
                                                                    row.is_payment_checked
                                                                        ? colors
                                                                            .greenAccent[400]
                                                                        : colors.grey[500],
                                                            }}
                                                        >
                                                            {row.is_payment_checked
                                                                ? "Conferido"
                                                                : "Pendente"}
                                                        </Typography>
                                                    </Stack>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Stack
                                                    spacing={
                                                        0.2
                                                    }
                                                >
                                                    <Typography
                                                        fontWeight={
                                                            900
                                                        }
                                                        color={
                                                            rowIsPaid
                                                                ? colors
                                                                    .greenAccent[400]
                                                                : colors
                                                                    .grey[200]
                                                        }
                                                    >
                                                        {formatDateTimeBR(
                                                            paymentDate
                                                        )}
                                                    </Typography>

                                                    {!row.last_transaction_paid_at &&
                                                        row.last_transaction_created_at && (
                                                            <Typography
                                                                variant="caption"
                                                                color={
                                                                    colors
                                                                        .grey[500]
                                                                }
                                                            >
                                                                Data da cobrança
                                                            </Typography>
                                                        )}
                                                </Stack>
                                            </TableCell>

                                            <TableCell align="right">
                                                <Typography
                                                    fontWeight={
                                                        900
                                                    }
                                                    sx={{
                                                        color:
                                                            received >
                                                                0
                                                                ? colors
                                                                    .greenAccent[400]
                                                                : colors
                                                                    .grey[500],
                                                    }}
                                                >
                                                    {moneyBRL(
                                                        received
                                                    )}
                                                </Typography>
                                            </TableCell>

                                            <TableCell align="center">
                                                <Chip
                                                    size="small"
                                                    icon={
                                                        statusUI.icon
                                                    }
                                                    label={
                                                        row.last_transaction_status_label ||
                                                        statusUI.label
                                                    }
                                                    sx={{
                                                        fontWeight:
                                                            900,
                                                        background:
                                                            statusUI.background,
                                                        color:
                                                            statusUI.color,
                                                        border: `1px solid ${statusUI.border}`,
                                                        ".MuiChip-icon":
                                                        {
                                                            color: `${statusUI.color} !important`,
                                                        },
                                                    }}
                                                />
                                            </TableCell>

                                            <TableCell align="center">
                                                {row.last_payment_method ? (
                                                    <Chip
                                                        size="small"
                                                        icon={
                                                            paymentUI.icon
                                                        }
                                                        label={
                                                            row.last_payment_method_label ||
                                                            paymentUI.label
                                                        }
                                                        sx={{
                                                            fontWeight:
                                                                800,
                                                            bgcolor:
                                                                paymentUI.background,
                                                            color:
                                                                paymentUI.color,
                                                            border: `1px solid ${paymentUI.border}`,
                                                            ".MuiChip-icon":
                                                            {
                                                                color: `${paymentUI.color} !important`,
                                                            },
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography
                                                        color={
                                                            colors
                                                                .grey[500]
                                                        }
                                                    >
                                                        -
                                                    </Typography>
                                                )}
                                            </TableCell>

                                            <TableCell>
                                                <Stack
                                                    direction="row"
                                                    spacing={
                                                        1
                                                    }
                                                    alignItems="center"
                                                >
                                                    <Avatar
                                                        sx={{
                                                            width: 32,
                                                            height: 32,
                                                            bgcolor:
                                                                alpha(
                                                                    "#F59E0B",
                                                                    0.14
                                                                ),
                                                            color:
                                                                "#F59E0B",
                                                        }}
                                                    >
                                                        <ReceiptLongOutlinedIcon
                                                            sx={{
                                                                fontSize: 17,
                                                            }}
                                                        />
                                                    </Avatar>

                                                    <Box>
                                                        <Typography
                                                            fontWeight={
                                                                900
                                                            }
                                                            color={
                                                                colors
                                                                    .grey[100]
                                                            }
                                                        >
                                                            {row.order_code ||
                                                                "-"}
                                                        </Typography>

                                                        <Typography
                                                            variant="caption"
                                                            color={
                                                                colors
                                                                    .grey[500]
                                                            }
                                                        >
                                                            {row.items_count ||
                                                                0}{" "}
                                                            item(ns)
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </TableCell>

                                            <TableCell>
                                                <Stack
                                                    spacing={
                                                        0.35
                                                    }
                                                >
                                                    <Stack
                                                        direction="row"
                                                        spacing={
                                                            0.7
                                                        }
                                                        alignItems="center"
                                                    >
                                                        <PersonOutlineOutlinedIcon
                                                            sx={{
                                                                fontSize: 16,
                                                                color:
                                                                    colors
                                                                        .grey[400],
                                                            }}
                                                        />

                                                        <Typography
                                                            fontWeight={
                                                                800
                                                            }
                                                            color={
                                                                colors
                                                                    .grey[100]
                                                            }
                                                        >
                                                            {row.customer_name ||
                                                                "-"}
                                                        </Typography>
                                                    </Stack>

                                                    <Stack
                                                        direction="row"
                                                        spacing={
                                                            0.7
                                                        }
                                                        alignItems="center"
                                                    >
                                                        <PhoneOutlinedIcon
                                                            sx={{
                                                                fontSize: 14,
                                                                color:
                                                                    colors
                                                                        .grey[500],
                                                            }}
                                                        />

                                                        <Typography
                                                            variant="caption"
                                                            color={
                                                                colors
                                                                    .grey[400]
                                                            }
                                                        >
                                                            {row.customer_phone ||
                                                                "-"}
                                                        </Typography>
                                                    </Stack>
                                                </Stack>
                                            </TableCell>

                                            <TableCell>
                                                <Stack
                                                    direction="row"
                                                    spacing={
                                                        0.8
                                                    }
                                                    alignItems="center"
                                                >
                                                    <StorefrontOutlinedIcon
                                                        sx={{
                                                            fontSize: 18,
                                                            color:
                                                                "#EC4899",
                                                        }}
                                                    />

                                                    <Typography
                                                        fontWeight={
                                                            800
                                                        }
                                                        color={
                                                            colors
                                                                .grey[100]
                                                        }
                                                    >
                                                        {sellerName}
                                                    </Typography>
                                                </Stack>
                                            </TableCell>

                                            <TableCell align="right">
                                                <Typography
                                                    fontWeight={
                                                        800
                                                    }
                                                    color={
                                                        colors
                                                            .grey[200]
                                                    }
                                                >
                                                    {moneyBRL(
                                                        row.order_total
                                                    )}
                                                </Typography>
                                            </TableCell>

                                            <TableCell align="right">
                                                <Typography
                                                    fontWeight={
                                                        800
                                                    }
                                                    color={
                                                        colors
                                                            .grey[200]
                                                    }
                                                >
                                                    {moneyBRL(
                                                        row.total_charged
                                                    )}
                                                </Typography>

                                                {Number(
                                                    row.transactions_count ||
                                                    0
                                                ) >
                                                    1 && (
                                                        <Typography
                                                            variant="caption"
                                                            color={
                                                                colors
                                                                    .grey[500]
                                                            }
                                                        >
                                                            {
                                                                row.transactions_count
                                                            }{" "}
                                                            cobranças
                                                        </Typography>
                                                    )}
                                            </TableCell>

                                            <TableCell align="right">
                                                <Typography
                                                    fontWeight={
                                                        900
                                                    }
                                                    sx={{
                                                        color:
                                                            balance >
                                                                0
                                                                ? "#F59E0B"
                                                                : colors
                                                                    .greenAccent[400],
                                                    }}
                                                >
                                                    {moneyBRL(
                                                        balance
                                                    )}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                }
                            )}

                            {paginatedRows.length ===
                                0 && (
                                    <TableRow>
                                        <TableCell
                                            colSpan={
                                                10
                                            }
                                            align="center"
                                            sx={{
                                                py: 7,
                                            }}
                                        >
                                            <Stack
                                                alignItems="center"
                                                spacing={
                                                    1
                                                }
                                            >
                                                <Avatar
                                                    sx={{
                                                        width: 52,
                                                        height: 52,
                                                        bgcolor:
                                                            alpha(
                                                                colors
                                                                    .grey[300],
                                                                0.08
                                                            ),
                                                        color:
                                                            colors
                                                                .grey[400],
                                                    }}
                                                >
                                                    <SearchIcon />
                                                </Avatar>

                                                <Typography
                                                    color={
                                                        colors
                                                            .grey[300]
                                                    }
                                                    fontWeight={
                                                        900
                                                    }
                                                >
                                                    Nenhum pagamento encontrado
                                                </Typography>

                                                <Typography
                                                    color={
                                                        colors
                                                            .grey[500]
                                                    }
                                                    variant="body2"
                                                >
                                                    Altere os filtros para consultar outros registros.
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={
                        filteredRows.length
                    }
                    page={page}
                    onPageChange={(
                        event,
                        newPage
                    ) =>
                        setPage(newPage)
                    }
                    rowsPerPage={
                        rowsPerPage
                    }
                    onRowsPerPageChange={(
                        event
                    ) => {
                        setRowsPerPage(
                            Number(
                                event.target
                                    .value
                            )
                        );

                        setPage(0);
                    }}
                    rowsPerPageOptions={[
                        10,
                        20,
                        50,
                        100,
                    ]}
                    labelRowsPerPage="Linhas por página"
                    labelDisplayedRows={({
                        from,
                        to,
                        count,
                    }) =>
                        `${from}-${to} de ${count}`
                    }
                    sx={{
                        borderTop: `1px solid ${alpha(
                            colors.grey[100],
                            0.08
                        )}`,
                    }}
                />
            </Paper>
        </Box>
    );
}

